import type { ReactNode } from "react";
import { createContext, useContext, useState, useEffect } from "react";

import type { Direction } from "@aviary/types";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useLocation } from "@shared/react-router-dom/useLocation/useLocation";
import type { DrawerAnalyticTitles } from "@shared/types/OverlayAnalyticTitles";

interface OverlayContext {
  isOpen: boolean;
  openDirection?: Direction;
  overlayTitle: string | null;
  analyticsName?: string;
  trackButtonClicks?: boolean;
  registerTitleElement: (el: HTMLElement | null) => void;
}

const OverlayContext = createContext<OverlayContext | null>(null);

export const useOverlayContext = () => useContext(OverlayContext);

export const OverlayProvider = ({
  children,
  isOpen,
  type,
  openDirection,
  analyticsName,
  analyticsTitle,
  trackButtonClicks = false,
}: {
  children: ReactNode;
  isOpen: boolean;
  type: "drawer" | "modal" | "dropdown";
  openDirection?: Direction;
  analyticsName?: string;
  analyticsTitle?: DrawerAnalyticTitles | string;
  trackButtonClicks?: boolean;
}) => {
  const [overlayTitle, setOverlayTitle] = useState<string>(analyticsTitle ?? "");

  const registerTitleElement = (titleElement: HTMLElement | null) => {
    if (analyticsTitle) return;

    const title =
      titleElement?.querySelector("h1, h2, h3, h4, h5, h6")?.textContent?.trim() ??
      titleElement?.firstElementChild?.textContent?.trim() ??
      titleElement?.textContent?.trim()?.split(/\s+/)?.slice(0, 3)?.join(" ") ??
      `untitled_${type}`;

    setOverlayTitle(title);
  };

  const logAnalyticsEvent = useAnalyticsLogger();
  const { pathname } = useLocation();

  const getRouteRole = () => {
    if (!pathname) return "";

    const namespace = pathname.split("/").filter(Boolean)[0];

    if (!namespace) return "";

    if (namespace === "u") {
      return "patient";
    } else if (namespace === "o") {
      return "store";
    } else if (namespace === "emerson") {
      return "emerson";
    } else if (namespace === "p") {
      return "practitioner";
    } else {
      return namespace;
    }
  };

  const generateName = () => {
    if (analyticsName) return analyticsName;

    const platformRole = getRouteRole();
    const title = overlayTitle.toLowerCase().replace(/\s+/g, "_");

    return `${platformRole}_${title}_${type}`;
  };

  const value = {
    isOpen,
    openDirection,
    analyticsName: generateName(),
    overlayTitle: overlayTitle || null,
    trackButtonClicks,
    registerTitleElement,
  };

  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = requestAnimationFrame?.(() => {
      logAnalyticsEvent({
        eventName: AnalyticsEvent.OVERLAY_VIEW,
        payload: {
          type,
          page_url: window.location.href,
          open_direction: openDirection,
          referrer: document.referrer,
          name: generateName(),
          title: overlayTitle,
        },
      });
    });

    return () => cancelAnimationFrame(timeoutId);
  }, [isOpen, overlayTitle, analyticsName]);

  return <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>;
};
